import React, { useState, useEffect } from 'react';
import { Type, AlignLeft, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * FormFieldsViewer - Employee UI for filling out custom form fields
 * Renders Text Input and Text Area fields based on admin configuration
 * Handles validation and collects responses
 */
const FormFieldsViewer = ({
    content,
    responses = {},
    onResponsesChange,
    readOnly = false,
    showValidation = false,
    validationErrors = {}
}) => {
    const fields = content?.fields || [];
    const instructions = content?.instructions || '';

    // Handle field value change
    const handleFieldChange = (fieldId, value) => {
        if (readOnly) return;
        
        const updated = {
            ...responses,
            [fieldId]: value
        };
        onResponsesChange(updated);
    };

    // Check if a field has an error
    const hasError = (fieldId) => {
        return showValidation && validationErrors[fieldId];
    };

    // Get field value
    const getFieldValue = (fieldId) => {
        return responses[fieldId] || '';
    };

    if (fields.length === 0) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <p className="text-gray-500">No form fields configured for this section.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Instructions */}
            {instructions && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <p className="text-sm text-blue-800">{instructions}</p>
                </div>
            )}

            {/* Form Fields */}
            <div className="space-y-5">
                {fields.map((field, index) => {
                    const fieldValue = getFieldValue(field.id);
                    const fieldHasError = hasError(field.id);
                    const isComplete = fieldValue && fieldValue.trim() !== '';

                    return (
                        <div key={field.id} className="relative">
                            {/* Label */}
                            <label 
                                htmlFor={field.id}
                                className="flex items-center gap-2 text-sm font-semibold text-gray-800 mb-2"
                            >
                                <span>{field.label || `Field ${index + 1}`}</span>
                                {field.required && (
                                    <span className="text-red-500 text-xs">*</span>
                                )}
                                {isComplete && !readOnly && (
                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                )}
                            </label>

                            {/* Input Field */}
                            {field.type === 'text_input' ? (
                                <input
                                    id={field.id}
                                    type="text"
                                    value={fieldValue}
                                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                    placeholder={field.placeholder || 'Enter your response...'}
                                    disabled={readOnly}
                                    className={`
                                        w-full px-4 py-3 border rounded-lg text-base transition-all
                                        min-h-[44px]
                                        ${readOnly 
                                            ? 'bg-gray-50 text-gray-700 border-gray-200 cursor-not-allowed' 
                                            : 'bg-white border-gray-300 focus:ring-2 focus:ring-coral-400 focus:border-coral-400'
                                        }
                                        ${fieldHasError 
                                            ? 'border-red-400 bg-red-50 focus:ring-red-400 focus:border-red-400' 
                                            : ''
                                        }
                                    `}
                                    style={{
                                        // Coral highlight on focus (using inline style for custom color)
                                        '--tw-ring-color': fieldHasError ? '#f87171' : '#f97316'
                                    }}
                                />
                            ) : (
                                <textarea
                                    id={field.id}
                                    value={fieldValue}
                                    onChange={(e) => handleFieldChange(field.id, e.target.value)}
                                    placeholder={field.placeholder || 'Enter your response...'}
                                    disabled={readOnly}
                                    rows="4"
                                    className={`
                                        w-full px-4 py-3 border rounded-lg text-base transition-all resize-none
                                        ${readOnly 
                                            ? 'bg-gray-50 text-gray-700 border-gray-200 cursor-not-allowed' 
                                            : 'bg-white border-gray-300 focus:ring-2 focus:ring-coral-400 focus:border-coral-400'
                                        }
                                        ${fieldHasError 
                                            ? 'border-red-400 bg-red-50 focus:ring-red-400 focus:border-red-400' 
                                            : ''
                                        }
                                    `}
                                />
                            )}

                            {/* Error Message */}
                            {fieldHasError && (
                                <div className="flex items-center gap-1.5 mt-2 text-red-600">
                                    <AlertCircle className="w-4 h-4" />
                                    <span className="text-sm">{validationErrors[field.id]}</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Completion Summary (for read-only mode) */}
            {readOnly && Object.keys(responses).length > 0 && (
                <div className="bg-green-50 border border-green-100 rounded-lg p-4 mt-4">
                    <div className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Form completed</span>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Utility function to validate form fields
 * Returns an object with field IDs as keys and error messages as values
 */
export const validateFormFields = (fields, responses) => {
    const errors = {};
    
    if (!fields || fields.length === 0) return errors;

    fields.forEach(field => {
        if (field.required) {
            const value = responses[field.id];
            if (!value || value.trim() === '') {
                errors[field.id] = `${field.label || 'This field'} is required`;
            }
        }
    });

    return errors;
};

/**
 * Utility function to package form responses for submission
 * Returns an array of { field_id, label, value } objects
 */
export const packageFormResponses = (fields, responses) => {
    if (!fields || fields.length === 0) return [];

    return fields.map(field => ({
        field_id: field.id,
        label: field.label,
        type: field.type,
        value: responses[field.id] || '',
        required: field.required
    }));
};

export default FormFieldsViewer;
